import { Box, CssBaseline } from '@mui/material';
import React, { useEffect } from 'react';

import { useDispatch } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { DataService } from '@/data/DataService';
import { seedSnapshot } from '@/data/seed';
import { fetchClientsThunk } from '@/store/clientsSlice';
import { importSnapshot as importSnapshotAction } from '@/store/slices/domainSlice';

import { DEFAULT_SIDEBAR, SIDEBAR_W, SIDEBAR_COLLAPSED_W } from '../components/v2/defaults';
import SidebarProfileV2 from '../components/v2/SidebarProfileV2';
import SidebarV2 from '../components/v2/SidebarV2';
import TopBarV2 from '../components/v2/TopBarV2';
import { GhostBanner } from '@company/shared-ui';
import { StorageAdapter } from '@/services/auth/StorageAdapter';
import { getAdminAppUrl } from '../config/runtimeUrls';

const SIDEBAR_COLLAPSED_KEY = 'sidebar-collapsed';

import { useAuth } from '../contexts/AuthContext';
import { CasesProvider } from '../contexts/CasesContext';
import { ClientsProvider } from '../contexts/ClientsContext';
import { DocumentContextProvider } from '../contexts/DocumentContext';
import { TasksProvider } from '../contexts/TasksContext';


const MainLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(() => {
    // Initialize from localStorage
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
      return saved === 'true';
    }
    return false;
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { user, hasPermission } = useAuth() as any;
  const isAdmin = Boolean(
    (hasPermission && typeof hasPermission === 'function' && hasPermission('admin')) ||
      (user && String(user.role).toLowerCase().includes('admin'))
  );
  const adminAppUrl = getAdminAppUrl();

  // Calculate sidebar width based on collapsed state
  const currentSidebarWidth = sidebarCollapsed ? SIDEBAR_COLLAPSED_W : SIDEBAR_W;

  // Handle sidebar collapse change with localStorage persistence
  const handleSidebarCollapsedChange = (collapsed: boolean) => {
    setSidebarCollapsed(collapsed);
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
  };

  const dispatch = useDispatch();
  const didFetchRef = React.useRef(false);

  useEffect(() => {
    // ✅ FIX: Single initialization guard to prevent duplicate API calls
    if (didFetchRef.current) return;
    didFetchRef.current = true;

    (async () => {
      try {
        const snap = await DataService.getOrInit(seedSnapshot);
        dispatch(importSnapshotAction(snap));
        try {
          (window as any).__domainClients = snap?.clients || [];
        } catch (_) {}
      } catch {
        dispatch(importSnapshotAction(seedSnapshot));
        try {
          (window as any).__domainClients = (seedSnapshot as any)?.clients || [];
        } catch (_) {}
      }
      // ✅ REMOVED: fetchClientsThunk call - ClientsTab and contexts handle their own fetching
      // This prevents triple-fetching of clients data
    })();
  }, [dispatch]);

  return (
    <CasesProvider>
      <ClientsProvider>
        <TasksProvider>
          <DocumentContextProvider>
            <style>{`
              @media (max-width: 767px) {
                .grid-layout {
                  grid-template-columns: 0 1fr !important;
                }
              }
              
              /* Smooth transition for grid layout */
              .grid-layout {
                transition: grid-template-columns 300ms ease-in-out;
              }
            `}</style>
            {user?.isImpersonated && (
              <GhostBanner
                impersonatorName={user.impersonatorId}
                targetName={user.name}
                onExit={() => {
                  try {
                    const storage = new StorageAdapter();
                    storage.clear();
                  } catch (_) {}
                  window.location.href = adminAppUrl;
                }}
              />
            )}
            <div
              className="min-h-screen grid theme-2025 grid-layout"
              style={{ gridTemplateColumns: `${currentSidebarWidth}px 1fr` }}
            >
              <CssBaseline />

              <aside className="border-r border-neutral-200 dark:border-neutral-700 bg-background/60 dark:bg-neutral-900/60 hidden md:block transition-colors duration-300">
                {/* New V2 Sidebar (desktop) */}
                <SidebarV2
                  items={DEFAULT_SIDEBAR}
                  activePath={location.pathname}
                  onNavigate={(p: string) => navigate(p)}
                  isAdmin={isAdmin}
                  isCollapsed={sidebarCollapsed}
                  onCollapsedChange={handleSidebarCollapsedChange}
                />
              </aside>

              {/* Mobile Sidebar Drawer */}
              {sidebarOpen && (
                <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true">
                  <div
                    className="absolute inset-0 bg-black/30"
                    onClick={() => setSidebarOpen(false)}
                  />
                  <div className="absolute left-0 top-0 h-full w-[80%] max-w-[300px] bg-white shadow-xl">
                    <div className="h-16 border-b border-slate-200" />
                    <div className="p-3">
                      <SidebarProfileV2
                        name={user?.name || 'User'}
                        role={user?.role || 'Role'}
                        isAdmin={isAdmin}
                        onSettings={() => {
                          navigate('/settings');
                          setSidebarOpen(false);
                        }}
                      />
                      <nav className="mt-3 space-y-1">
                        {DEFAULT_SIDEBAR.map(it => (
                          <button
                            key={it.path}
                            className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm hover:bg-slate-50"
                            onClick={() => {
                              navigate(it.path);
                              setSidebarOpen(false);
                            }}
                          >
                            <span className="text-brand-600">{it.icon}</span>
                            <span>{it.label}</span>
                          </button>
                        ))}
                      </nav>
                    </div>
                  </div>
                </div>
              )}

              <main className="w-full h-screen flex flex-col bg-white dark:bg-neutral-900 text-slate-900 dark:text-neutral-100 overflow-hidden transition-colors duration-300">
                {/* TopBar - Fixed */}
                <div className="flex-shrink-0 h-16">
                  <TopBarV2
                    onBurger={() => setSidebarOpen(true)}
                    onSearch={(q?: string) => {
                      const query = (q || '').trim();
                      if (query) navigate(`/search?q=${encodeURIComponent(query)}`);
                    }}
                    isSidebarCollapsed={sidebarCollapsed}
                  />
                </div>

                {/* Content - Scrollable */}
                <div className="flex-1 overflow-y-auto w-full">
                  <Outlet />
                </div>
              </main>
            </div>
          </DocumentContextProvider>
        </TasksProvider>
      </ClientsProvider>
    </CasesProvider>
  );
};

export default MainLayout;
